import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check, ArrowUpRight } from "lucide-react";
import { IMAGES, STUDENT_SUPPORT, EXTERNAL_LINKS } from "@/lib/site-data";

export default function StudyAbroad() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-36">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Image with spotlight framing */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] border border-border shadow-2xl shadow-secondary/20">
            <motion.img
              style={{ y }}
              src={IMAGES.students}
              alt="Students studying abroad"
              className="h-[116%] w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-2 sm:left-6 bg-primary text-primary-foreground rounded-2xl px-6 py-5 shadow-xl">
            <div className="font-display text-4xl font-semibold">100%</div>
            <div className="text-xs uppercase tracking-wider opacity-90 mt-1">Support, end to end</div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-xs uppercase tracking-[0.3em] text-primary font-medium">
            Study Abroad, Simplified
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-secondary mt-4 leading-[1.05]">
            More than admissions — a complete student ecosystem.
          </h2>
          <p className="text-foreground/70 mt-6 text-lg leading-relaxed max-w-xl">
            From your first counselling session in Bhubaneswar to settling into
            your campus abroad, we handle the details that matter. Transparent,
            outcome-focused, and always in your corner.
          </p>

          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-8">
            {STUDENT_SUPPORT.map((s) => (
              <div key={s} className="flex items-center gap-3" data-testid="support-item">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary/12 flex items-center justify-center">
                  <Check size={14} className="text-primary" />
                </span>
                <span className="text-sm text-foreground/80">{s}</span>
              </div>
            ))}
          </div>

          <a
            href={EXTERNAL_LINKS.msmunify}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="studyabroad-cta"
            className="inline-flex items-center gap-2 mt-10 text-secondary font-medium border-b-2 border-primary pb-1 hover:gap-3 transition-all"
          >
            Explore the MSM Unify platform <ArrowUpRight size={18} className="text-primary" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
